import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";

interface PickedUser {
  email: string;
  name: string;
  jobTitle?: string | null;
  department?: string | null;
}

/**
 * Type-ahead picker backed by /api/users/search (Microsoft Graph).
 * Returns the canonical mail + display name so callers don't have to handle UPNs.
 */
export function EntraUserPicker({
  value,
  onChange,
  placeholder = "Search Entra ID for a name or email",
  autoFocus = false,
}: {
  value: string;
  onChange: (picked: PickedUser | null, raw: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [q, setQ] = useState(value);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset internal text when parent clears value.
  useEffect(() => { setQ(value); }, [value]);

  useEffect(() => {
    function clickOutside(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", clickOutside);
    return () => document.removeEventListener("mousedown", clickOutside);
  }, []);

  function runSearch(term: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (term.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.searchUsers(term.trim());
        setResults(r?.users ?? []);
      } finally { setLoading(false); }
    }, 200);
  }

  function pick(u: any) {
    const email = (u.mail ?? u.userPrincipalName ?? "").toLowerCase();
    setQ(email);
    setOpen(false);
    onChange({
      email,
      name: u.displayName ?? email,
      jobTitle: u.jobTitle ?? null,
      department: u.department ?? null,
    }, email);
  }

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        value={q}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => {
          const v = e.target.value;
          setQ(v);
          setOpen(true);
          runSearch(v);
          // Let parent see raw text in case the picker isn't used and they type a full address.
          onChange(null, v);
        }}
        onFocus={() => { setOpen(true); if (q && q.length >= 2) runSearch(q); }}
        className="w-full px-3 py-2 border border-slate-200 rounded text-sm"
      />
      {open && (q.trim().length >= 2) && (
        <div className="absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
          {loading && <div className="p-3 text-xs text-slate-500">Searching Entra ID...</div>}
          {!loading && results.length === 0 && (
            <div className="p-3 text-xs text-slate-500">No matches in Entra ID. You can still type a full email above.</div>
          )}
          {results.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => pick(u)}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
            >
              <div className="text-sm font-medium text-liaise-navy">{u.displayName}</div>
              <div className="text-xs text-slate-500">
                {u.mail ?? u.userPrincipalName}
                {u.jobTitle && <span className="ml-2 text-slate-400">· {u.jobTitle}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
